<script lang="ts">
  import { getStepper, TONE_PALETTE, type LifecycleStage, type LifecycleTone } from '../lib/donationLifecycle';

  // The donation's current lifecycle stage (from the backend `lifecycle_stage` field).
  export let stage: LifecycleStage = 'submitted';

  $: steps = getStepper(stage);

  // A done/current/cancelled step shows its stage tone (shared palette); upcoming stays neutral grey.
  function colorFor(step: { tone: LifecycleTone; state: string }): string {
    return step.state === 'upcoming' ? '#d1d5db' : TONE_PALETTE[step.tone].solid;
  }
</script>

<ol class="stepper" aria-label="Donation progress">
  {#each steps as step, i (step.key)}
    {@const color = colorFor(step)}
    <li class="step {step.state}">
      <div
        class="marker"
        aria-hidden="true"
        style={step.state === 'done' || step.state === 'cancelled'
          ? `background:${color};border-color:${color};color:#fff`
          : step.state === 'current'
            ? `background:#fff;border-color:${color};color:${color}`
            : `background:#fff;border-color:#d1d5db;color:#9ca3af`}
      >
        {#if step.state === 'done'}
          <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
            <path fill-rule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.1 3.1 6.8-6.8a1 1 0 011.4 0z" clip-rule="evenodd"/>
          </svg>
        {:else if step.state === 'cancelled'}
          <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
            <path fill-rule="evenodd" d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z" clip-rule="evenodd"/>
          </svg>
        {:else}
          <span class="dot-number">{i + 1}</span>
        {/if}
      </div>
      <span class="label" class:active={step.state === 'current' || step.state === 'cancelled'}>{step.label}</span>
      {#if i < steps.length - 1}
        <span class="connector" aria-hidden="true" style={step.state === 'done' ? `background:${color}` : 'background:#e5e7eb'}></span>
      {/if}
    </li>
  {/each}
</ol>

<style>
  .stepper {
    display: flex;
    align-items: flex-start;
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  .step {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-width: 0;
  }

  .marker {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    z-index: 1;
    border: 2px solid #d1d5db;
  }

  .label {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
    line-height: 1.2;
    max-width: 9rem;
  }

  .label.active {
    color: #111827;
    font-weight: 600;
  }

  /* Connector line to the next step */
  .connector {
    position: absolute;
    top: 13px;
    left: 50%;
    width: 100%;
    height: 2px;
    background: #e5e7eb;
    z-index: 0;
  }

  @media (max-width: 640px) {
    .stepper {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }
    .step {
      flex-direction: row;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
    }
    .label {
      margin-top: 0;
      text-align: left;
      max-width: none;
    }
    .connector {
      display: none;
    }
  }
</style>
